export const meta = {
  id: '27-infinitive',
  order: 27,
  titleFi: 'Infinitiivi',
  titleEn: 'Infinitive',
  duolingoUnit: 'duo-32-infinitive',
  // + Sanapaja's own words this lesson first needs (docs/COURSE.md "Own words in the lessons"; waves/wave-NN/lessons.json)
  lemmaIds: ['kostagon', 'kostilus', 'kostōba', 'sylugon', 'rakegon', 'gīmigon', 'sytilībagon', 'bēvilagon', 'sahagon', 'dāeremagon', 'qrīdrughagon', 'rijībagon', 'ours:ārēdegon', 'ours:uēpūljagon', 'ours:lumiljagon', 'ours:pāsikagon', 'ours:mazotāpagon', 'ours:kostilluragon', 'ours:jakenon', 'ours:paeremagon', 'ours:keliemvos', 'ours:dorolvemagon', 'ours:indiruragon', 'ours:ūnnon', 'ours:harpi', 'ours:bandi', 'ours:gitara', 'ours:qysȳbagon', 'ours:teknika', 'ours:matti', 'ours:golfi', 'ours:bēzboli'],
  exampleIds: [
    'ex-322d35e66806',
    'ex-51b07f61553a',
    'hotd-s1e07-011',
    'ex-460c7f7f58fd',
    'ex-e2193b671e2a',
    'hotd-s2e04-003',
    'ex-d2b199ca0a0e',
    'ex-bf8ed497ebd5',
    'hotd-s3e01-002',
    'ex-c3fb271660ee',
    'ex-3883ad1ce004',
    'ex-ad69940236f4',
  ],
  sources: [
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Infinitive',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar#Infinitive',
    'https://wiki.languageinvention.com/index.php?title=User:Najahho/High_Valyrian_Verb_Moods#Uses_of_the_infinitive',
    'https://wiki.languageinvention.com/index.php?title=B%C4%93vilagon',
    'https://wiki.languageinvention.com/index.php?title=Sytil%C4%ABbagon',
    'https://wiki.languageinvention.com/index.php?title=Sahagon',
    'https://archiveofourown.org/works/41199249/chapters/105725109',
  ],
  // the week (docs/COURSE.md "Lesson hub")
  grammar: ['infinitives'],
  grammarAlso: ['valence'],
  showLines: ['hotd-s1e07-011', 'hotd-s2e04-003', 'hotd-s3e01-002'],
  pronunciation: { focus: 'q', words: ['qrīdrughagon', 'jēnqēpsa', 'jēnqa', 'raqiros', 'qeldlie'] },
  letter: {
    fi: 'Kerro, mitä osaat, mitä haluat ja mitä yrität tehdä.',
    en: 'Write what you can do, what you want to do and what you try to do.',
  },
  scene: 'hotd-s2e04-1',
}

# Infinitiivi

Infinitiivi on verbin muoto, joka nimeää teon yleisesti – kertomatta, kuka sen tekee. Se on myös sanakirjamuoto, aivan kuten suomen *tehdä*, *nähdä* ja *puhua*. Valyrian infinitiivi päättyy vokaalivartaloisissa verbeissä **-gon** ja konsonanttivartaloisissa **-agon**: <Hv glossed>urnegon</Hv> ’nähdä’, <Hv glossed>ipradagon</Hv> ’syödä’.

Yhden käyttötavan tunnet jo: käskyn, joka annetaan jollekulle muulle kuin puhuteltavalle. Siihen riittävät infinitiivi ja tekijä vokatiivissa:

<Example id="ex-322d35e66806" />

Tällä oppitunnilla infinitiivi pääsee varsinaiseen tehtäväänsä: se täydentää toista verbiä.

## Yhteinen subjekti: voida, yrittää, osata

Moni verbi voi saada tai vaatii rinnalleen toisen verbin infinitiivin. Kaava on tämä:

**(nominit) · (infinitiivi) · (pääverbi)**

Tuttu esimerkki on <Hv glossed>kostagon</Hv> ’voida, pystyä’. Sama juuri on sanoissa <Hv glossed>kostilus</Hv> ’ole hyvä, kenties’ ja <Hv glossed>kostōba</Hv> ’voimakas’. Katso, mitä lauseelle tapahtuu, kun siihen lisätään ’voida’:

<Interlinear hv="Taob|a riñ|e urne|s." gloss="poika-NOM tyttö-ACC nähdä-PRS.3SG" fi="Poika näkee tytön." />

<Interlinear hv="Taob|a riñ|e urnegon kost|as." gloss="poika-NOM tyttö-ACC nähdä-INF voida-PRS.3SG" fi="Poika voi nähdä tytön." />

Subjekti ja objekti pysyvät paikoillaan ja samoissa sijoissa. Verbistä tulee infinitiivi, ja persoonapääte siirtyy lauseen loppuun uuteen pääverbiin. Tips & Notes kuvaa tunnelman kauniisti: infinitiivi kertoo, ettei lause ole vielä valmis – on luettava eteenpäin, jotta koko ajatus selviää.

Suomi auttaa tässä paljon. Mekin sanomme *poika voi nähdä tytön*: objekti on samassa sijassa kuin ilman voida-verbiä, ja persoona näkyy pääverbissä. Ero on järjestyksessä. Suomessa pääverbi tulee ensin ja infinitiivi perässä, valyriassa infinitiivi ensin ja pääverbi aivan lopussa.

<Pronounce word="kostagon" />

<Example id="ex-51b07f61553a" />

<Line id="hotd-s1e07-011" />

Rhaenyran repliikki on kielteinen, joten pääverbi on subjunktiivissa (<Hv fi="en voi">koston daor</Hv>). Infinitiivi pysyy ennallaan.

<Note>
Peterson on itse sanonut repliikin *mērpī*-sanan virheeksi: se on adjektiivin muoto, ja adverbina oikea muoto olisi <Hv l="mērprī">mērprī</Hv> tai <Hv l="mērprī">mērpirī</Hv> (Petersonin Conlang Dialogue -selitykset, 2022).
</Note>

Samalla tavalla toimivat:

- <Hv glossed>sylugon</Hv> ’yrittää’
- <Hv glossed>rakegon</Hv> ’ottaa osaa, osallistua’
- <Hv glossed>gīmigon</Hv> ’tietää’ – infinitiivin kanssa **’osata’**, kuten suomen *osata lukea*

<Example id="ex-460c7f7f58fd" />

<Example id="ex-e2193b671e2a" />

Nuoren Rhaenyran syytöksissä on kaksi tämän oppitunnin asiaa: <Hv glossed>qrīdrūdā</Hv> ’hylkäsit’ (epäsäännöllinen perfekti, josta lisää alempana) ja lopussa <Hv glossed>ospryjagon sylutā</Hv> ’yritit tuhota’.

<Line id="hotd-s2e04-003" />

## Kun infinitiivin tekijä on pääverbin objekti

Pieni joukko verbejä toimii toisin. Ne ottavat infinitiivin tekijän **omaksi objektikseen**, ja kaava on tämä:

**(muut nominit) · (infinitiivi) · (infinitiivin tekijä) · (pääverbi)**

Tekijän sija riippuu pääverbistä.

### Sytilībagon – ’pitäisi’

<Hv>Sytilībagon</Hv> ilmaisee heikkoa velvollisuutta. Sitä käytetään yleensä **yksikön 3. persoonassa ilman omaa subjektia**, eikä se taivu kenenkään mukaan. Sen objekti eli se, jonka pitäisi tehdä jotakin, on **akkusatiivissa** ja seisoo heti verbin edellä. Objektin edellä on infinitiivi ja vielä sitä ennen muut lauseen osat.

<Interlinear hv="Ābr|e ȳdragon val|e sytilīb|as." gloss="nainen-ACC puhua-INF mies-ACC pitäisi-PRS.3SG" fi="Miehen pitäisi puhua naiselle." />

Verbi ei muutu, vaikka tekijä vaihtuu: <Hv glossed>Ābre ȳdragon avy sytilības</Hv> ’Sinun pitäisi puhua naiselle.’ Toinen mahdollinen käännös on ’olla jonkun asia’: *on sinun asiasi puhua naiselle*.

Tässä suomi on lähempänä kuin koskaan aiemmin. Meidän *pitäisi* ja *täytyy* toimivat juuri näin: ne ovat aina yksikön 3. persoonassa (*minun pitäisi, sinun pitäisi, hänen pitäisi*), eikä tekijä ole subjekti. Ero on sijassa: suomessa tekijä on genetiivissä, <Hv>sytilībagon</Hv>-verbin kanssa akkusatiivissa.

<Pronounce word="sytilībagon" />

<Example id="ex-d2b199ca0a0e" />

<Example id="ex-bf8ed497ebd5" />

Jälkimmäisessä on paljon tuttua: perfekti <Hv glossed>sytilīptas</Hv>, joka on tässä käännetty ’olisi pitänyt’, ja instrumentaali <Hv>laodiaposo</Hv>, koska verbi <Hv>jomīsagon</Hv> ottaa puettavan naamion objektikseen instrumentaalissa.

### Bēvilagon – ’täytyy’

<Hv>Bēvilagon</Hv> ilmaisee vahvaa velvollisuutta. Se toimii kuten <Hv>sytilībagon</Hv>, mutta verbin edellä oleva tekijä on **genetiivissä**.

<Interlinear hv="Dār|i rijagon azant|o bēvilza." gloss="kuningas-ACC ylistää-INF ritari-GEN täytyä-PRS.3SG" fi="Ritarin täytyy ylistää kuningasta." />

<Compare fi="ritari|n" hv="azant|o" label="genetiivi + täytyy ~ genetiivi + bēvilagon" />

Tämä on melkein suomea sellaisenaan: *ritarin täytyy* – sama genetiivi ja sama 3. persoonan verbi, joka ei taivu tekijän mukaan. Vain järjestys on valyrialainen: infinitiivi, tekijä, pääverbi.

<Line id="hotd-s3e01-002" />

<Pronounce word="bēvilagon" />

<Note>
Tips & Notes mainitsee sulkeissa myös datiivin, ja sanakirjan mukaan verbin perussija on datiivi ja genetiivi on vaihtoehto silloin, kun tekijä seisoo heti verbin edellä. Ohjeiden esimerkissä tekijä on kuitenkin genetiivissä, samoin kaikissa tämän oppitunnin virallisissa lauseissa. Käytä siis genetiiviä.
</Note>

## Sahagon – pakottaa, teettää

Valyriassa on monenlaisia teettäviä eli kausatiivisia verbejä, mutta <Hv fi="pakottaa, teettää">sahagon</Hv> sopii mihin tahansa teettävään rakenteeseen. Se taipuu tavalliseen tapaan, ja sijat jakautuvat näin:

- **teettäjä** (se, joka pakottaa) on **nominatiivissa** – se on pääverbin subjekti
- **tekijä** (se, joka pannaan tekemään) on **datiivissa**
- muut lauseen osat ovat samoissa sijoissa kuin ilman verbiä <Hv fi="pakottaa">sahagon</Hv>

<Interlinear hv="Dār|ys azant|ot zaldrīz|ī idakogon sētas." gloss="kuningas-NOM ritari-DAT lohikäärme-ACC hyökätä-INF pakottaa-PRF.3SG" fi="Kuningas pakotti ritarin hyökkäämään lohikäärmeen kimppuun." />

Suomessa sama rakenne on tuttu *-tta*-verbeistä: *teetin työn palvelijalla*, *harjautin hevoseni palvelijalla*. Meillä tekijä on adessiivissa, valyriassa datiivissa – kummassakin kielessä se on siirtynyt pois subjektin paikalta toiseen sijaan.

<Example id="ex-c3fb271660ee" />

<Example id="ex-3883ad1ce004" />

Rhaenyran pyynnössä tekijä on <Hv glossed>ynot</Hv> ’minulle’ (datiivi), infinitiivi on <Hv glossed>sagon</Hv> ’olla’, ja <Hv glossed>ābrazȳrys aōhys</Hv> ’sinun vaimosi’ on nominatiivissa, koska se on infinitiivin predikatiivi: *pane minut olemaan vaimosi*.

Tips & Notesin mukaan verbin preesensvartalo on **sah-** ja perfektivartalo **sēt-**: <Hv glossed>sētan</Hv> ’pakotin’, <Hv glossed>sētas</Hv> ’hän pakotti’. Imperatiivi <Hv fi="pane, pakota">sahās</Hv> näkyy yllä olevassa esimerkissä.

<Note>
Lähteet eroavat tässä. Sanakirjan taivutustaulukossa preesens on lyhyissä muodoissa ilman h:ta (<Hv fi="pakotan">sān</Hv>, <Hv fi="pakotat">sā</Hv>, <Hv l="sahagon" fi="pakottaa">sas</Hv>), kun taas Tips & Notes puhuu vartalosta sah-, ja virallinen käsky on <Hv fi="pane, pakota">sahās</Hv>. Perfektistä lähteet ovat yhtä mieltä.
</Note>

## Epäsäännöllisiä perfektivartaloita

Tämän oppitunnin sanastossa on viisi verbiä, joiden perfektivartalo kannattaa opetella erikseen:

- <Hv glossed>dāeremagon</Hv> ’vapauttaa’ → <Hv>dāerēd-</Hv>: <Hv glossed>dāerēdas</Hv> ’hän vapautti’
- <Hv glossed>sytilībagon</Hv> ’pitäisi’ → <Hv>sytilīpt-</Hv>: <Hv>sytilīptas</Hv>
- <Hv glossed>qrīdrughagon</Hv> ’hylätä’ → <Hv>qrīdrūd-</Hv>: <Hv l="qrīdrughagon">qrīdrūdas</Hv>
- <Hv glossed>rijībagon</Hv> ’palvoa, ylistää’ → <Hv>rijīpt-</Hv>: <Hv>rijīptas</Hv>
- <Hv glossed>kostagon</Hv> ’voida’ → <Hv>kōtt-</Hv>: <Hv>kōttas</Hv>

Niissä näkyvät perfektin oppitunnin säännöt: <Hv>b</Hv> muuttuu soinnittomaksi t:n edellä (<Hv>sytilīpt-</Hv>, <Hv>rijīpt-</Hv>), ja osassa vartaloista perfektin tunnuksena on d (<Hv>dāerēd-</Hv>, <Hv>qrīdrūd-</Hv>).

<Example id="ex-ad69940236f4" />

<Pronounce word="qrīdrughagon" />

## Harjoituslauseita

<Practice hv="Taoba ābre urnegon kostas." fi="Poika voi nähdä naisen." />

<Practice hv="Riña havon ipradagon sylus." fi="Tyttö yrittää syödä leivän." />

<Practice hv="Riña pikībagon gīmis." fi="Tyttö osaa lukea." />

<Practice hv="Ābre ȳdragon taobe sytilības." fi="Pojan pitäisi puhua naiselle." />

<Practice hv="Dāri rijagon taobo bēvilza." fi="Pojan täytyy ylistää kuningasta." />

<Practice hv="Vala taobot havon ipradagon sētas." fi="Mies pakotti pojan syömään leivän." />

## Kuka teki kenelle?

Valitse, kumpi suomennos on oikea. Sen kertovat päätteet, ei sanajärjestys.

<KukaTeki />

## Sanat

<Vocab />

<Check />
